import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useAddContact, useStudioTeam, useUpdateClient } from '@/api/studio';
import type { Client, ClientContact, ClientPatch } from '@/api/types';
import { Dialog } from '@/components/ui';
import { clientRoleLabel } from '@/lib/access';

const stageLabel: Record<Client['stage'], string> = { lead: 'Лид', proposal: 'Предложение', active: 'Работаем', paused: 'Пауза', former: 'Завершили работу' };
const editable: NonNullable<ClientPatch['stage']>[] = ['lead', 'active', 'paused'];

/** Name, stage and manager of the client: what the card could not change before (MK42-5831). */
export function ClientBasics({ client }: { client: Client }) {
  const update = useUpdateClient();
  const { data: team } = useStudioTeam();
  const [name, setName] = useState(client.name);
  const [stage, setStage] = useState<Client['stage']>(client.stage);
  const [managerId, setManagerId] = useState(client.manager.id);
  const managers = team?.filter((m) => !m.disabled && m.studioRole !== 'specialist') ?? [];
  const dirty = name.trim() !== client.name || stage !== client.stage || managerId !== client.manager.id;
  // A client taken off service is changed by nobody here; a stage the list does not offer stays as shown.
  const frozen = client.stage === 'former';

  function save() {
    const patch: ClientPatch = {};
    if (name.trim() !== client.name) patch.name = name.trim();
    if (stage !== client.stage && (editable as string[]).includes(stage)) patch.stage = stage as NonNullable<ClientPatch['stage']>;
    if (managerId !== client.manager.id) patch.manager_id = managerId;
    update.mutate({ id: client.id, patch });
  }

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold">Клиент</h2>
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && name.trim()) save();
        }}
      >
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Название</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} aria-label="Название клиента" />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Стадия</span>
          <select className="field" value={stage} onChange={(e) => setStage(e.target.value as Client['stage'])} disabled={frozen} aria-label="Стадия">
            {!editable.includes(client.stage as (typeof editable)[number]) && <option value={client.stage}>{stageLabel[client.stage]}</option>}
            {editable.map((k) => (
              <option key={k} value={k}>
                {stageLabel[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Ведёт</span>
          <select className="field" value={managerId} onChange={(e) => setManagerId(e.target.value)} aria-label="Ведёт клиента">
            {!managers.some((m) => m.id === client.manager.id) && <option value={client.manager.id}>{client.manager.name}</option>}
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        {update.error && (
          <p role="alert" className="text-sm text-overdue">
            {(update.error as Error).message}
          </p>
        )}
        <div>
          <button className="btn-primary" disabled={!dirty || !name.trim() || update.isPending}>
            Сохранить
          </button>
        </div>
      </form>
    </section>
  );
}

export function AddContact({ client }: { client: Client }) {
  const [open, setOpen] = useState(false);
  const add = useAddContact();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClientContact['role']>('member');
  const [notSent, setNotSent] = useState(false);
  const close = () => {
    setOpen(false);
    add.reset();
  };
  return (
    <>
      <button className="btn-ghost h-8 px-2 text-xs" onClick={() => setOpen(true)}>
        <UserPlus className="size-4" /> Добавить контакт
      </button>
      {notSent && <p className="mt-2 text-xs text-waiting">Контакт добавлен, но письмо не ушло: отправьте приглашение ещё раз.</p>}
      <Dialog open={open} onClose={close} title="Новый контакт клиента">
        <form
          className="space-y-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || !email.trim()) return;
            add.mutate(
              { clientId: client.id, input: { name: name.trim(), email: email.trim(), role } },
              {
                onSuccess: (r) => {
                  setNotSent(r.invited === false);
                  setName('');
                  setEmail('');
                  close();
                },
              },
            );
          }}
        >
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Имя" aria-label="Имя контакта" autoFocus />
          <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Почта" aria-label="Почта контакта" />
          <select className="field" value={role} onChange={(e) => setRole(e.target.value as ClientContact['role'])} aria-label="Роль контакта">
            {(Object.keys(clientRoleLabel) as ClientContact['role'][]).map((k) => (
              <option key={k} value={k}>
                {clientRoleLabel[k]}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted">На почту уйдёт ссылка для входа в кабинет клиента.</p>
          {add.error && (
            <p role="alert" className="text-sm text-overdue">
              {(add.error as Error).message}
            </p>
          )}
          <div className="flex justify-end border-t border-line pt-4">
            <button className="btn-primary" disabled={!name.trim() || !email.trim() || add.isPending}>
              Добавить и пригласить
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
